import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CreditCard,
  CheckCircle2,
  Zap,
  Users,
  BarChart3,
  Crown,
  Shield,
  ArrowRight,
  Calendar,
} from 'lucide-react';

const Pricing = () => {
  const navigate = useNavigate();

  const features = [
    { icon: Zap, title: 'Unlimited Uploads', description: 'Upload unlimited pitch decks and documents' },
    { icon: BarChart3, title: 'Advanced Analytics', description: 'Track viewer engagement and insights' },
    { icon: Crown, title: 'Custom Branding', description: 'Add your logo and customize colors' },
    { icon: Users, title: 'Team Collaboration', description: 'Invite team members to collaborate' },
    { icon: Shield, title: 'Priority Support', description: 'Get help when you need it most' },
    { icon: CreditCard, title: 'Secure Payments', description: 'Bank-level security for all transactions' },
  ];

  return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col py-12 px-4 sm:px-6 lg:px-8">
      <div className="absolute -z-10 w-64 h-64 sm:w-96 sm:h-96 bg-blue-500/20 rounded-full blur-3xl" />
      <div className="max-w-4xl mx-auto w-full flex-1">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white mb-4">Simple, affordable pricing</h1>
          <p className="text-lg text-slate-300 max-w-xl mx-auto">
            One plan. Everything you need to raise faster with one smart deck link.
          </p>
        </div>

        {/* Plan Card */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 sm:p-10 mb-10 hover:border-blue-500/30 transition-all duration-300">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8">
            <div>
              <h2 className="text-2xl font-semibold text-white mb-2">Basic</h2>
              <div className="flex items-baseline gap-2 mb-4">
                <span className="text-4xl font-bold text-white">$5</span>
                <span className="text-slate-400">/month</span>
              </div>
              <p className="text-slate-300 mb-6">
                Free trial to start. Cancel anytime. No commitment required.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2 text-slate-300">
                  <Calendar className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  <span>Free trial included</span>
                </li>
                <li className="flex items-center gap-2 text-slate-300">
                  <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>All features included</span>
                </li>
              </ul>
              <button
                onClick={() => navigate('/signup')}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-4 rounded-xl font-medium text-lg transition-all"
              >
                Get started
                <ArrowRight className="w-5 h-5" />
              </button>
              <p className="text-slate-500 text-sm mt-4">
                Already have an account?{' '}
                <button
                  onClick={() => navigate('/signin')}
                  className="text-blue-400 hover:text-blue-300 font-medium transition-colors"
                >
                  Sign in
                </button>
              </p>
            </div>
          </div>
        </div>

        {/* What's Included */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 sm:p-10 hover:border-blue-500/30 transition-all duration-300">
          <h2 className="text-2xl font-semibold text-white mb-6">What's included</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {features.map((item, index) => (
              <div key={index} className="flex items-start gap-3">
                <item.icon className="w-6 h-6 text-blue-400 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-white font-medium mb-1">{item.title}</h3>
                  <p className="text-slate-300 text-sm">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
